/**
 * OCC Well Cache Service
 * 
 * Provides offline caching for OCC (Oklahoma Corporation Commission) wells
 * using IndexedDB. Caches recently viewed wells for offline navigation.
 * 
 * Features:
 * - Cache up to 50 recently viewed OCC wells
 * - Automatic cache expiry (7 days)
 * - Offline access to well data
 * - Background sync when online
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { OCCCompanyWell } from '../intelligence/occCompanySearch';

// ============================================================================
// DATABASE SCHEMA
// ============================================================================

interface OCCWellCacheDB extends DBSchema {
  occ_wells_cache: {
    key: string; // well.id
    value: CachedOCCWell;
    indexes: {
      'by-cached-at': number;
      'by-distance': number;
    };
  };
  occ_favorites: {
    key: string; // well.id
    value: FavoriteOCCWell;
    indexes: {
      'by-added-at': number;
    };
  };
  occ_nearby_cache: {
    key: string; // cache key (lat-lng-radius)
    value: NearbyCacheEntry;
  };
}

interface CachedOCCWell extends OCCCompanyWell {
  cachedAt: number;
  lastViewedAt: number;
  viewCount: number;
}

interface FavoriteOCCWell extends OCCCompanyWell {
  addedAt: number;
  notes?: string;
}

interface NearbyCacheEntry {
  cacheKey: string;
  lat: number;
  lng: number;
  radiusMiles: number;
  wells: OCCCompanyWell[];
  cachedAt: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DB_NAME = 'oilwellx-occ-cache';
const DB_VERSION = 1;
const MAX_CACHED_WELLS = 50;
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const NEARBY_CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

// ============================================================================
// DATABASE INITIALIZATION
// ============================================================================

let dbPromise: Promise<IDBPDatabase<OCCWellCacheDB>> | null = null;

const getDB = async (): Promise<IDBPDatabase<OCCWellCacheDB>> => {
  if (!dbPromise) {
    dbPromise = openDB<OCCWellCacheDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // OCC Wells Cache Store
        if (!db.objectStoreNames.contains('occ_wells_cache')) {
          const wellsStore = db.createObjectStore('occ_wells_cache', { keyPath: 'id' });
          wellsStore.createIndex('by-cached-at', 'cachedAt');
          wellsStore.createIndex('by-distance', 'distance');
        }
        
        // Favorites Store
        if (!db.objectStoreNames.contains('occ_favorites')) {
          const favStore = db.createObjectStore('occ_favorites', { keyPath: 'id' });
          favStore.createIndex('by-added-at', 'addedAt');
        }
        
        // Nearby Cache Store
        if (!db.objectStoreNames.contains('occ_nearby_cache')) {
          db.createObjectStore('occ_nearby_cache', { keyPath: 'cacheKey' });
        }
      },
    });
  }
  return dbPromise;
};

// ============================================================================
// WELL CACHE OPERATIONS
// ============================================================================

/**
 * Cache a viewed OCC well for offline access
 */
export const cacheOCCWell = async (well: OCCCompanyWell): Promise<void> => {
  try {
    const db = await getDB();
    const existing = await db.get('occ_wells_cache', well.id);
    
    const cachedWell: CachedOCCWell = {
      ...well,
      cachedAt: existing?.cachedAt || Date.now(),
      lastViewedAt: Date.now(),
      viewCount: (existing?.viewCount || 0) + 1,
    };
    
    await db.put('occ_wells_cache', cachedWell);
    
    // Prune old entries if over limit
    await pruneCache();
  } catch (error) {
    console.error('Failed to cache OCC well:', error);
  }
};

/**
 * Cache multiple wells at once (e.g., after a search)
 */
export const cacheOCCWells = async (wells: OCCCompanyWell[]): Promise<void> => {
  try {
    const db = await getDB();
    const tx = db.transaction('occ_wells_cache', 'readwrite');
    
    for (const well of wells.slice(0, MAX_CACHED_WELLS)) {
      const existing = await tx.store.get(well.id);
      const cachedWell: CachedOCCWell = {
        ...well,
        cachedAt: existing?.cachedAt || Date.now(),
        lastViewedAt: Date.now(),
        viewCount: existing?.viewCount || 0,
      };
      await tx.store.put(cachedWell);
    }
    
    await tx.done;
    await pruneCache();
  } catch (error) {
    console.error('Failed to cache OCC wells:', error);
  }
};

/**
 * Get a cached well by ID (for offline access)
 */
export const getCachedWell = async (wellId: string): Promise<CachedOCCWell | undefined> => {
  try {
    const db = await getDB();
    return await db.get('occ_wells_cache', wellId);
  } catch (error) {
    console.error('Failed to get cached well:', error);
    return undefined;
  }
};

/**
 * Get all cached wells (for offline browsing)
 */
export const getAllCachedWells = async (): Promise<CachedOCCWell[]> => {
  try {
    const db = await getDB();
    const wells = await db.getAll('occ_wells_cache');
    // Filter expired and sort by last viewed
    const now = Date.now();
    return wells
      .filter(w => now - w.cachedAt < CACHE_EXPIRY_MS)
      .sort((a, b) => b.lastViewedAt - a.lastViewedAt);
  } catch (error) {
    console.error('Failed to get cached wells:', error);
    return [];
  }
};

/**
 * Prune cache to maintain MAX_CACHED_WELLS limit
 */
const pruneCache = async (): Promise<void> => {
  try {
    const db = await getDB();
    const wells = await db.getAllFromIndex('occ_wells_cache', 'by-cached-at');
    
    if (wells.length > MAX_CACHED_WELLS) {
      // Remove oldest entries
      const toRemove = wells.slice(0, wells.length - MAX_CACHED_WELLS);
      const tx = db.transaction('occ_wells_cache', 'readwrite');
      for (const well of toRemove) {
        // Don't remove favorites
        const isFavorite = await db.get('occ_favorites', well.id);
        if (!isFavorite) {
          await tx.store.delete(well.id);
        }
      }
      await tx.done;
    }
  } catch (error) {
    console.error('Failed to prune cache:', error);
  }
};

// ============================================================================
// FAVORITES OPERATIONS
// ============================================================================

/**
 * Add a well to favorites
 */
export const addFavorite = async (well: OCCCompanyWell, notes?: string): Promise<void> => {
  try {
    const db = await getDB();
    const favorite: FavoriteOCCWell = {
      ...well,
      addedAt: Date.now(),
      notes,
    };
    await db.put('occ_favorites', favorite);
    
    // Also cache the well for offline access
    await cacheOCCWell(well);
  } catch (error) {
    console.error('Failed to add favorite:', error);
    throw error;
  }
};

/**
 * Remove a well from favorites
 */
export const removeFavorite = async (wellId: string): Promise<void> => {
  try {
    const db = await getDB();
    await db.delete('occ_favorites', wellId);
  } catch (error) {
    console.error('Failed to remove favorite:', error);
    throw error;
  }
};

/**
 * Check if a well is favorited
 */
export const isFavorite = async (wellId: string): Promise<boolean> => {
  try {
    const db = await getDB();
    const favorite = await db.get('occ_favorites', wellId);
    return !!favorite;
  } catch (error) {
    return false;
  }
};

/**
 * Get all favorite wells
 */
export const getAllFavorites = async (): Promise<FavoriteOCCWell[]> => {
  try {
    const db = await getDB();
    const favorites = await db.getAllFromIndex('occ_favorites', 'by-added-at');
    return favorites.reverse(); // Most recent first
  } catch (error) {
    console.error('Failed to get favorites:', error);
    return [];
  }
};

/**
 * Toggle favorite status
 */
export const toggleFavorite = async (well: OCCCompanyWell): Promise<boolean> => {
  const currentlyFavorite = await isFavorite(well.id);
  if (currentlyFavorite) {
    await removeFavorite(well.id);
    return false;
  } else {
    await addFavorite(well);
    return true;
  }
};

// ============================================================================
// NEARBY CACHE OPERATIONS
// ============================================================================

/**
 * Cache nearby wells search results
 */
export const cacheNearbyWells = async (
  lat: number,
  lng: number,
  radiusMiles: number,
  wells: OCCCompanyWell[]
): Promise<void> => {
  try {
    const db = await getDB();
    const cacheKey = `${lat.toFixed(3)}-${lng.toFixed(3)}-${radiusMiles}`;
    
    const entry: NearbyCacheEntry = {
      cacheKey,
      lat,
      lng,
      radiusMiles,
      wells,
      cachedAt: Date.now(),
    };
    
    await db.put('occ_nearby_cache', entry);
    
    // Also cache individual wells
    await cacheOCCWells(wells);
  } catch (error) {
    console.error('Failed to cache nearby wells:', error);
  }
};

/**
 * Get cached nearby wells if still valid
 */
export const getCachedNearbyWells = async (
  lat: number,
  lng: number,
  radiusMiles: number
): Promise<OCCCompanyWell[] | null> => {
  try {
    const db = await getDB();
    const cacheKey = `${lat.toFixed(3)}-${lng.toFixed(3)}-${radiusMiles}`;
    const entry = await db.get('occ_nearby_cache', cacheKey);
    
    if (!entry) return null;
    
    // Check if cache is still valid
    if (Date.now() - entry.cachedAt > NEARBY_CACHE_EXPIRY_MS) {
      await db.delete('occ_nearby_cache', cacheKey);
      return null;
    }
    
    return entry.wells;
  } catch (error) {
    console.error('Failed to get cached nearby wells:', error);
    return null;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Clear all cached data
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    const db = await getDB();
    await db.clear('occ_wells_cache');
    await db.clear('occ_nearby_cache');
    // Note: Favorites are NOT cleared
  } catch (error) {
    console.error('Failed to clear cache:', error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async (): Promise<{
  cachedWells: number;
  favorites: number;
  nearbyCaches: number;
  totalSizeEstimate: string;
}> => {
  try {
    const db = await getDB();
    const cachedWells = await db.count('occ_wells_cache');
    const favorites = await db.count('occ_favorites');
    const nearbyCaches = await db.count('occ_nearby_cache');
    
    // Rough estimate: 2KB per well
    const sizeKB = (cachedWells + favorites) * 2;
    const totalSizeEstimate = sizeKB < 1024 
      ? `${sizeKB} KB` 
      : `${(sizeKB / 1024).toFixed(1)} MB`;
    
    return { cachedWells, favorites, nearbyCaches, totalSizeEstimate };
  } catch (error) {
    console.error('Failed to get cache stats:', error);
    return { cachedWells: 0, favorites: 0, nearbyCaches: 0, totalSizeEstimate: '0 KB' };
  }
};

/**
 * Check if we're offline
 */
export const isOffline = (): boolean => {
  return !navigator.onLine;
};

// Export default service object
export const occWellCacheService = {
  cacheOCCWell,
  cacheOCCWells,
  getCachedWell,
  getAllCachedWells,
  addFavorite,
  removeFavorite,
  isFavorite,
  getAllFavorites,
  toggleFavorite,
  cacheNearbyWells,
  getCachedNearbyWells,
  clearAllCache,
  getCacheStats,
  isOffline,
};

export default occWellCacheService;
